/**
 * PilotageHubController — hub `/pilotage` (EPIC-031 / 31.2, spec §16 ;
 * synthèse live livrée en 99.64).
 *
 * Charge `GET /pilotage/hub` au connect() et hydrate les 3 tuiles
 * avec une synthèse réelle :
 *  - Concentration §16.B : client le plus exposé + part de CA + pastille
 *    de palier (info / warning / critical).
 *  - TJM §16.C : TJM cible €/j (mention brouillon si profil non
 *    enregistré).
 *  - Trajectoire CA §16.E : écart relatif YTD réel vs objectif annuel
 *    (signé, vert si ≥ 0, gris sinon — pas de couleur agressive même en
 *    retard) ; sous-titre rappelant l'objectif annuel. (Remplace
 *    l'ancienne tuile trésorerie §16.A, retirée au profit de Finances
 *    §31.)
 *
 * Le statut « compte bancaire dédié » §16.D et son alerte sont
 * disponibles dans la payload mais pas affichés ici : la grille
 * `pilotage.html` ne prévoit pas de bandeau dédié. Le statut est
 * édité depuis `/profil-entreprise.html` onglet « Banque dédiée »
 * (controller `profile-entreprise-bank`, EPIC-007 / 7.3).
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS ; on mute le
 * `textContent` / `classList` des targets, ou on clone des nœuds DOM via
 * `createElement` pour la pastille structurée.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";

const EURO_0 = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const PERCENT = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const PALIER_PILL_CLASSES = {
  info: "nm-pill nm-pill--ochre",
  warning: "nm-pill nm-pill--red",
  critical: "nm-pill nm-pill--red",
};

const PALIER_LABELS = {
  info: "Surveiller",
  warning: "Élevée",
  critical: "Critique",
};

export default class PilotageHubController extends BaseController {
  static targets = [
    "tile",
    "concentrationName",
    "concentrationStatus",
    "concentrationSub",
    "tjmName",
    "tjmStatus",
    "tjmSub",
    "trajectoryName",
    "trajectoryStatus",
    "trajectorySub",
  ];

  async connect() {
    try {
      const hub = await api.getPilotageHub();
      this._hydrate(hub);
    } catch (error) {
      // 401 déjà géré globalement par api.js (redirect /connexion.html).
      // Sur erreur transitoire, on laisse les placeholders de chargement
      // — pas de toast bloquant sur un hub d'aperçu.
      console.warn("[pilotage-hub] hydration failed", error);
    }
  }

  _hydrate(hub) {
    if (!hub || typeof hub !== "object") return;
    this._hydrateConcentration(hub.concentrationSummary);
    this._hydrateTjm(hub.tjmSummary);
    this._hydrateTrajectory(hub.trajectorySummary);
  }

  _hydrateTrajectory(summary) {
    if (!summary || typeof summary !== "object") return;
    if (summary.isEmpty) {
      this._setText("trajectoryName", "Aucun objectif défini");
      this._setColor("trajectoryName", null);
      this._setText("trajectorySub", "Définissez votre objectif annuel");
      return;
    }
    if (summary.gapPct === null || summary.gapPct === undefined) {
      // Objectif YTD attendu nul → écart non significatif (§16.E).
      this._setText("trajectoryName", "À suivre");
      this._setColor("trajectoryName", null);
    } else {
      const gap = Number(summary.gapPct) || 0;
      this._setText("trajectoryName", this._formatSignedPercent(gap));
      // Neutre même en retard (§16.E — pas de couleur agressive).
      this._setColor("trajectoryName", gap >= 0 ? "text-success" : "text-muted");
    }
    const target = Number(summary.targetHt) || 0;
    this._setText("trajectorySub", `Objectif annuel : ${EURO_0.format(target)} €`);
  }

  _hydrateConcentration(summary) {
    if (!summary || typeof summary !== "object") return;
    if (summary.isEmpty || summary.topClientName === null) {
      this._setText("concentrationName", "Pas encore assez de clients");
      this._clearConcentrationPill();
      this._setText("concentrationSub", "À partir de 3 clients facturés");
      return;
    }
    const pct = (Number(summary.sharePercent) || 0) * 100;
    this._setText(
      "concentrationName",
      `${summary.topClientName} — ${PERCENT.format(pct)} %`,
    );
    this._renderConcentrationPill(summary.level);
    this._setText("concentrationSub", "Part du CA sur 12 mois glissants");
  }

  _hydrateTjm(summary) {
    if (!summary || typeof summary !== "object") return;
    if (summary.targetTjm === null) {
      this._setText("tjmName", "À définir");
      this._setText("tjmSub", "Renseignez votre objectif de revenu");
      return;
    }
    const tjm = Number(summary.targetTjm) || 0;
    this._setText("tjmName", `${EURO_0.format(tjm)} €`);
    this._setText(
      "tjmSub",
      summary.isDraft
        ? "Estimation par défaut (brouillon)"
        : "Selon votre objectif de revenu",
    );
  }

  _renderConcentrationPill(level) {
    if (!this.hasConcentrationStatusTarget) return;
    const target = this.concentrationStatusTarget;
    target.replaceChildren();
    if (!level || !PALIER_PILL_CLASSES[level]) return;
    const pill = document.createElement("span");
    pill.className = PALIER_PILL_CLASSES[level];
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode(PALIER_LABELS[level] ?? level));
    target.appendChild(pill);
  }

  _clearConcentrationPill() {
    if (!this.hasConcentrationStatusTarget) return;
    this.concentrationStatusTarget.replaceChildren();
  }

  _formatSignedPercent(value) {
    const sign = value >= 0 ? "+ " : "− ";
    return `${sign}${PERCENT.format(Math.abs(value))} %`;
  }

  _setColor(targetName, className) {
    const key = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[key]) return;
    const target = this[`${targetName}Target`];
    target.classList.remove("text-success", "text-muted");
    if (className) target.classList.add(className);
  }

  _setText(targetName, value) {
    const key = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`;
    if (!this[key]) return;
    const target = this[`${targetName}Target`];
    target.textContent = value;
  }
}
